import { useState } from "react";
import { usePeopleSearch } from "../data";
import { IcPlus } from "./icons";

/**
 * Attaching something to a person, in one field.
 *
 * Written out twice already — once in the new-follow-up form and again for
 * filing a captured note — and the second copy is where the two would start to
 * disagree about what counts as a match. It leans on `usePeopleSearch`, so it
 * finds people by any part of the name or by phone, which is the same behaviour
 * as the search box on the People screen.
 *
 * `null` is a legitimate answer. "Order more candles before Chanukah" is a real
 * reminder and belongs to nobody; forcing a person onto it would attach it to
 * whoever happened to be on screen.
 */
export function PersonPicker({
  personId,
  personName,
  onPick,
  disabled,
  placeholder = "חפשו לפי שם (אפשר להשאיר ריק)",
}: {
  personId: string | null;
  personName: string;
  onPick: (person: { id: string; name: string } | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [term, setTerm] = useState("");
  const search = usePeopleSearch(term);

  if (personId) {
    return (
      <div className="btn-row">
        <span className="grow">{personName || "ללא שם"}</span>
        <button className="btn" disabled={disabled} onClick={() => onPick(null)}>
          הסרה
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        className="form-input"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {/* Six, not fifty. This is a picker, not a directory — if the right person
          is not in the first six the answer is to type another letter. */}
      {term.trim().length >= 2 &&
        search.data.slice(0, 6).map((p) => (
          <button
            key={p.id}
            className="list-row"
            style={{ width: "100%", textAlign: "start" }}
            onClick={() => {
              onPick({ id: p.id, name: p.displayName ?? "" });
              setTerm("");
            }}
          >
            <span className="grow">{p.displayName ?? "ללא שם"}</span>
            <IcPlus size={15} />
          </button>
        ))}
      {term.trim().length >= 2 && !search.loading && search.data.length === 0 && (
        <p className="faint" style={{ margin: "6px 0 0" }}>
          לא מצאנו אף אחד בשם הזה.
        </p>
      )}
    </>
  );
}
